import { Capacitor } from '@capacitor/core';
import { CapacitorSQLite, SQLiteConnection } from '@capacitor-community/sqlite';

const DB_NAME = 'cashbook.db';

// The list of migrations, in order. The number prefixes determine the version.
const MIGRATIONS = [
    '1.sql',
    '2.sql'
];

class NativeStorageService {
    _db = null;
    _sqlite = null;

    constructor() {
        if (Capacitor.isNativePlatform()) {
            this._sqlite = new SQLiteConnection(CapacitorSQLite);
        }
    }

    async initializeDatabase() {
        if (!this._sqlite) {
            console.log("SQLite is not available on this platform.");
            return;
        }
        try {
            this._db = await this._sqlite.createConnection(DB_NAME, false, 'no-encryption', 1, false);
            await this._db.open();
            await this._runMigrations();
            console.log('Database initialized and migrations completed.');
        } catch (e) {
            console.error('Error initializing database', e);
            throw e; // Rethrow to notify the app's startup process
        }
    }

    async _runMigrations() {
        // Get the current version
        const versionResult = await this._db.query('PRAGMA user_version;');
        let currentVersion = versionResult.values[0].user_version || 0;
        console.log(`DB version: ${currentVersion}`);

        for (const migrationFile of MIGRATIONS) {
            const migrationVersion = parseInt(migrationFile.split('.')[0], 10);

            if (migrationVersion > currentVersion) {
                console.log(`Running migration ${migrationFile}...`);
                try {
                    const response = await fetch(`/migrations/${migrationFile}`);
                    if (!response.ok) {
                        throw new Error(`Could not fetch migration file: ${migrationFile}`);
                    }
                    const sql = await response.text();
                    
                    await this._db.execute(sql);

                    // Set the new version using PRAGMA
                    await this._db.execute(`PRAGMA user_version = ${migrationVersion}`);
                    
                    console.log(`DB version updated to: ${migrationVersion}`);
                } catch (e) {
                    console.error(`Migration ${migrationFile} failed!`, e);
                    throw e;
                }
            }
        }
    }

    async getDb() {
        if (!this._db) {
            await this.initializeDatabase();
        }
        return this._db;
    }

    async getTransactions(userId = null, date = new Date()) {
        const db = await this.getDb();
        let sql = "SELECT * FROM transactions WHERE is_deleted = 0";
        const values = [];

        // 如果 date 不為 null，則進行月份過濾
        if (date !== null) {
            const year = date.getFullYear();
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const yyyy_mm = `${year}-${month}`;
            sql += " AND strftime('%Y-%m', date) = ?";
            values.push(yyyy_mm);
        }

        if (userId) {
            sql += " AND user_id = ?";
            values.push(userId);
        } else {
            sql += " AND user_id IS NULL";
        }
        
        sql += " ORDER BY date DESC, id DESC";
        
        const result = await db.query(sql, values);
        return result.values || [];
    }

    async addTransaction(tx) {
        const db = await this.getDb();
        const now = new Date().toISOString();
        const sql = `
            INSERT INTO transactions (user_id, amount, description, type, date, category, created_at, last_modified, sync_status)
            VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?);
        `;
        const values = [tx.user_id || null, tx.amount, tx.description, tx.type, tx.date, tx.category, now, now, 'new'];
        const res = await db.run(sql, values);
        return res.changes.lastId;
    }
    
    async updateTransaction(id, tx) {
        const db = await this.getDb();
        const now = new Date().toISOString();
        const sql = `
            UPDATE transactions
            SET amount = ?, description = ?, type = ?, date = ?, category = ?, last_modified = ?, sync_status = ?
            WHERE id = ?;
        `;
        const values = [tx.amount, tx.description, tx.type, tx.date, tx.category, now, 'modified', id];
        await db.run(sql, values);
    }

    async deleteTransaction(id) {
        const db = await this.getDb();
        const now = new Date().toISOString();
        const sql = `
            UPDATE transactions
            SET is_deleted = 1, last_modified = ?, sync_status = 'modified'
            WHERE id = ?;
        `;
        await db.run(sql, [now, id]);
    }

    async getUnsyncedTransactions(userId) {
        if (!userId) return [];
        const db = await this.getDb();
        const result = await db.query("SELECT * FROM transactions WHERE sync_status != 'synced' AND user_id = ?", [userId]);
        return result.values || [];
    }

    async upsertTransactionsFromServer(transactions) {
        const db = await this.getDb();
        
        const upsertSql = `
            INSERT INTO transactions (server_id, user_id, amount, description, type, date, category, created_at, server_modified_at, last_modified, sync_status, is_deleted)
            VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 'synced', ?)
            ON CONFLICT(server_id) DO UPDATE SET
                amount = excluded.amount,
                description = excluded.description,
                type = excluded.type,
                date = excluded.date,
                category = excluded.category,
                server_modified_at = excluded.server_modified_at,
                last_modified = excluded.last_modified,
                sync_status = 'synced',
                is_deleted = excluded.is_deleted
            WHERE excluded.server_modified_at > transactions.server_modified_at;
        `;

        await db.beginTransaction();
        try {
            for (const tx of transactions) {
                const now = new Date().toISOString();
                const values = [
                    tx.id, tx.user_id, tx.amount, tx.description, tx.type, tx.date,
                    tx.category, tx.created_at, tx.modified_at, now, tx.is_deleted || 0
                ];
                await db.run(upsertSql, values);
            }
            await db.commitTransaction();
        } catch (e) {
            await db.rollbackTransaction();
            console.error("Error upserting transactions from server", e);
            throw e;
        }
    }

    async markTransactionsAsSynced(updates) {
        const db = await this.getDb();
        const now = new Date().toISOString();
        const sql = `UPDATE transactions SET server_id = ?, sync_status = 'synced', last_modified = ? WHERE id = ?`;

        await db.beginTransaction();
        try {
            for (const update of updates) {
                await db.run(sql, [update.server_id, now, update.local_id]);
            }
            await db.commitTransaction();
        } catch (e) {
            await db.rollbackTransaction();
            console.error("Error marking transactions as synced", e);
            throw e;
        }
    }
}

export const nativeStorageService = new NativeStorageService();
